/**
 * Класс для работы с определенным значением в хранилище.
 *
 * Главное отличие от стандартного Storage в том, что работа ведется в контексте одного значения,
 * т.е. указав в параметрах конфигурации ключ (storageKey) и хранилище (storage),
 * нет необходимости последующие разы указывать данные параметры для получения/установки значения.
 *
 * Приведение примера на хранилище sessionStorage:
 *
 * Стандартная реализация:
 *    sessionStorage.getItem('key');
 *    sessionStorage.setItem('key', 'value');
 *
 * Предоставляемая классом ItemStore реализация:
 *    const tokenStore = new ItemStore<number>('key', sessionStorage);
 *    tokenStore.getItem();
 *    tokenStore.setItem(value);
 *
 * Дополнительные возможности:
 * - поддерживает указание типа данных для методов getItem и setItem.
 */
export class ItemStore<T> {
    /**
     * Ключ по которому сохраняется значение в хранилище.
     */
    private readonly storageKey: string;

    /**
     * Хранилище для работы с данными.
     */
    private readonly storage: Storage;

    /**
     * @param storageKey - ключ по которому сохраняется значение в хранилище.
     * @param storage - хранилище для работы с данными.
     */
    constructor(storageKey: string, storage: Storage) {
        this.storageKey = storageKey;
        this.storage = storage;
    }

    /**
     * Получение значения из хранилища по заданному ключу.
     *
     * @return значение из хранилища, если значение не найдено = null.
     */
    getItem(): T {
        const item = this.storage.getItem(this.storageKey);
        return JSON.parse(item);
    }

    /**
     * Установка значения в хранилище по заданному ключу.
     *
     * @param value значение, которое необходимо установить.
     * @throws {Error} при пустом значении.
     */
    setItem(value: T): void {
        if (!value) {
            throw new Error('Value must be specified');
        }

        this.storage.setItem(this.storageKey, JSON.stringify(value));
    }

    /**
     * Удаление значения из хранилища по заданному ключу.
     */
    removeItem(): void {
        this.storage.removeItem(this.storageKey);
    }

    /**
     * Проверка на существование значения в хранилище по заданному ключу.
     *
     * @return true - значение существует, false - значение не существует.
     */
    itemExists(): boolean {
        return !!this.getItem();
    }
}
